import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { HERO_IMAGE } from "@/data";

export default function Hero() {
  const [start, setStart] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  useEffect(() => {
    const t = setTimeout(() => setStart(true), 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen overflow-hidden flex items-end"
      data-testid="hero"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMAGE} alt="Steel scaffolding structure against sky" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 w-full mx-auto max-w-[1600px] px-6 lg:px-12 pb-20 lg:pb-28 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: start ? 1 : 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-8 h-px bg-[#FF4D00]" aria-hidden="true" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4D00]">
            Canadian Scaffolding & Construction — Est. 2007
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter text-6xl sm:text-7xl lg:text-[9rem]">
          <MaskLines lines={["Engineered", "To Endure"]} start={start} />
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="font-mono font-light text-[#A3A3A3] max-w-md text-base leading-relaxed"
          >
            We raise the steel that raises cities. Precision access scaffolding,
            shoring and heavy-civil formwork — built square, braced beyond code,
            and left standing.
          </motion.p>

          <motion.a
            href="#contact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="group inline-flex items-center gap-3 bg-[#FF4D00] text-black font-mono uppercase tracking-[0.15em] text-sm px-8 py-5 hover:bg-white transition-colors w-fit"
            data-testid="hero-cta"
          >
            Start Your Build
            <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
}
